import { useCallback, useEffect, useState } from 'react';
import ReactLoading from 'react-loading';
import { useSearchParams } from 'react-router-dom';
import BookingStats from '../../../pages/supBooking/bookingStats/BookingStats';
import SupBookingStay from '../../../pages/supBooking/bookingTypes/SupBookingStay';
import Pagination from '../../pagination/Pagination';
import { orgStayBookingsFun, orgStayCancelFun, orgStayListingsFun } from '../../../api/orgCampAPI';
import errorHandle from '../../../utils/errorHandle';

/*
 * Organizer > Camp > Bookings: the same table as Support > Bookings > Camp
 * (cancel = full refund for website / app bookings), for the organizer's
 * own stays. New bookings come from the header's "Add Booking".
 */
const OrgCampBookings = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const date = searchParams.get('date') || 'upcoming';
    const listingId = searchParams.get('stay') || '';
    const pageNumber = Number(searchParams.get('page')) || 1;
    const limit = 10;
    const offSet = (pageNumber - 1) * limit;
    const [listings, setListings] = useState([]);
    const [bookings, setBookings] = useState([]);
    const [stats, setStats] = useState({});
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);
    const reload = () => setRefreshKey((k) => k + 1);

    const updateURL = useCallback(
        (params) => {
            const current = Object.fromEntries([...searchParams]);
            Object.keys(params).forEach((key) => {
                if (params[key] === '' || params[key] === null) delete current[key];
                else current[key] = params[key];
            });
            setSearchParams(current);
        },
        [searchParams, setSearchParams]
    );

    useEffect(() => {
        orgStayListingsFun()
            .then((res) => setListings(res?.data || []))
            .catch(errorHandle);
    }, []);

    // a booking added from the header's "Add Booking" — show it
    useEffect(() => {
        window.addEventListener('sup-booking-added', reload);
        return () => window.removeEventListener('sup-booking-added', reload);
    }, []);

    useEffect(() => {
        let gone = false;
        setLoading(true);
        orgStayBookingsFun({ date, listingId, limit, offset: offSet })
            .then((res) => {
                if (gone) return;
                setBookings(res?.data?.data || []);
                setStats(res?.data?.stats || {});
                setTotalCount(res?.data?.totalCount || 0);
            })
            .catch((error) => !gone && errorHandle(error))
            .finally(() => !gone && setLoading(false));
        return () => {
            gone = true;
        };
    }, [date, listingId, offSet, refreshKey]);

    const paginationClick = (number) => {
        if (Math.ceil(totalCount / limit) > 1) {
            updateURL({ page: number });
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    return (
        <div className='dashboard'>
            {loading && (
                <div className='loadingBackground'>
                    <ReactLoading type='spin' color='#006aff' className='loadingSpin' />
                </div>
            )}
            <BookingStats stats={stats} />
            <div className='customOptions'>
                <div className='sticky-container'>
                    <select
                        className='select'
                        aria-label='Stay'
                        value={listingId}
                        onChange={(e) => updateURL({ stay: e.target.value, page: 1 })}
                    >
                        <option value=''>All stays</option>
                        {listings.map((l) => (
                            <option key={l._id} value={l._id}>
                                {l.title}
                            </option>
                        ))}
                    </select>
                </div>
                <div className='sticky-container'>
                    <select
                        className='select'
                        aria-label='Bookings'
                        value={date}
                        onChange={(e) => updateURL({ date: e.target.value, page: 1 })}
                    >
                        <option value='upcoming'>Upcoming</option>
                        <option value='completed'>Completed</option>
                        <option value='all'>All</option>
                    </select>
                </div>
            </div>
            <SupBookingStay bookingData={bookings} onChanged={reload} cancelBooking={orgStayCancelFun} />
            <Pagination
                paginationClick={paginationClick}
                numberOfRows={totalCount}
                offSet={offSet}
                pageNumber={pageNumber}
                limit={limit}
            />
        </div>
    );
};

export default OrgCampBookings;
